/**
 * Changing your own password.
 *
 * Reachable by every signed-in operator, not just administrators — an account
 * whose holder cannot change its password is stuck with whatever they were
 * handed, and for the seeded account that is a password printed in this
 * repository's README.
 *
 * The current password is asked for even though the session is already
 * authenticated. That is what stops a lifted session token from becoming a
 * permanently stolen account.
 */
import { useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField,
} from '@mui/material';

import { accessApi, MIN_PASSWORD_LENGTH } from '../api/access';

export function ChangePasswordDialog({
  open, onClose, onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const ready = current.length > 0 && next.length >= MIN_PASSWORD_LENGTH && next === confirm;

  const close = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
    onClose();
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await accessApi.changeOwnPassword(current, next);
      setSaving(false);
      close();
      onDone('Your password has been changed.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change your password.');
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="xs" onClose={close}>
      <DialogTitle>Change your password</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Current password"
            size="small"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoFocus
            fullWidth
          />
          <TextField
            label="New password"
            size="small"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            error={tooShort}
            helperText={`At least ${MIN_PASSWORD_LENGTH} characters.`}
            fullWidth
          />
          <TextField
            label="Confirm new password"
            size="small"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={mismatch}
            helperText={mismatch ? 'These do not match.' : ' '}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" disabled={saving || !ready} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Change password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
